(function () {
  "use strict";

  angular
    .module("alk.routeExceptions")
    .service("routeExceptionsLayerFactory", routeExceptionsLayerFactory);

  routeExceptionsLayerFactory.$inject = [
    "mapService",
    "mapUtilities"
  ];

  function routeExceptionsLayerFactory(mapService, mapUtilities) {
    return function (layer, featuresByAFIDCache, featuresByGroupIDCache) {
      return {
        clearAllRouteExceptions: () => clearAllRouteExceptions(layer, featuresByAFIDCache, featuresByGroupIDCache),
        addRouteExceptionsToMap: _.curry(addRouteExceptionsToMap)(layer, featuresByAFIDCache, featuresByGroupIDCache),
        frameAllExceptions: () => frameAllExceptions(layer),
        frameRouteException: _.curry(frameRouteException)(featuresByAFIDCache, featuresByGroupIDCache),
        frameRouteExceptions: _.curry(frameRouteExceptions)(featuresByAFIDCache, featuresByGroupIDCache),
        flush: () => flush(layer, featuresByAFIDCache, featuresByGroupIDCache),
        numberOfFeatures: () => numberOfFeatures(layer),
        isAvoidFavorFeature: isAvoidFavorFeature
      };
    };

    ///////////////////

    function flush(layer, featuresByAvoidFavorID, featuresByGroupID) {
      layer.removeAllFeatures();
      _.forOwn(featuresByAvoidFavorID, (_, k) => delete featuresByAvoidFavorID[k]);
      _.forOwn(featuresByGroupID, (_, k) => delete featuresByGroupID[k]);
    }

    function numberOfFeatures(layer) {
      return layer.features.length;
    }

    function isAvoidFavorFeature(feature) {
      return !_.isUndefined(feature.attributes.isAvoidFavorFeature);
    }

    function addGroupedRouteExceptionsToMap(routeExceptionsLayer, featuresByGroupID, groupedExceptions, set) {
      var featuresWithGroupIDs = _.chain(groupedExceptions)
        .filter((g) => !featuresByGroupID.hasOwnProperty(g.GroupID))
        .map((g) => {
          var features = _.chain(g.GroupLinks)
            .map((e) => getFeatureVector(e.Geometries, g, e.AvoidFavorID, g.GroupID, set))
            .value();
          return {
            id: g.GroupID,
            features: features
          };
        })
        .value();

      // for all of the new features that we're going to add,
      // cache them by the groupID
      _.forEach(featuresWithGroupIDs, (o) => featuresByGroupID[o.id] = o.features);

      var featuresToAdd = _.chain(featuresWithGroupIDs)
        .map((o) => o.features)
        .flatten()
        .value();

      routeExceptionsLayer.addFeatures(featuresToAdd);
    }

    function addUngroupedRouteExceptionsToMap(routeExceptionsLayer, featuresByAvoidFavorID, ungroupedExceptions, set) {
      // convert each ungrouped route exception into an object
      // that contains its avoid favor id and a list of features.
      // we will cache each list of features by ID for this layer.
      var featuresWithIDs = _.chain(ungroupedExceptions)
        .map((e) => {
          var geometries = e.GroupLinks[0].Geometries;
          return {
            id: e.GroupLinks[0].AvoidFavorID,
            features: [getFeatureVector(geometries, e, e.GroupLinks[0].AvoidFavorID, null, set)]
          };
        })
        .filter((o) => !featuresByAvoidFavorID.hasOwnProperty(o.id))
        .value();

      // for all of the new features that we're going to add,
      // cache them.
      _.forEach(featuresWithIDs, (o) => featuresByAvoidFavorID[o.id] = o.features);

      // convert each {id, features} object into a list of features,
      // and flatten.
      // too bad _.flatMap isn't available until lodash 4.0.0 :(
      var featuresToAdd = _.chain(featuresWithIDs)
        .map((o) => o.features)
        .flatten()
        .value();

      routeExceptionsLayer.addFeatures(featuresToAdd);
    }

    function addRouteExceptionsToMap(routeExceptionsLayer, featuresByAvoidFavorID, featuresByGroupID, routeExceptions, set) {
      var partitioned = _.partition(routeExceptions, "GroupID", null);
      var ungrouped = partitioned[0];
      var grouped = partitioned[1];

      addUngroupedRouteExceptionsToMap(routeExceptionsLayer, featuresByAvoidFavorID, ungrouped, set);
      addGroupedRouteExceptionsToMap(routeExceptionsLayer, featuresByGroupID, grouped, set);
    }

    function clearAllRouteExceptions(routeExceptionsLayer, featuresByAvoidFavorID, featuresByGroupID) {
      routeExceptionsLayer.removeAllFeatures();
      _.forEach(_.keys(featuresByAvoidFavorID), (k) => delete featuresByAvoidFavorID[k]);
      _.forEach(_.keys(featuresByGroupID), (k) => delete featuresByGroupID[k]);
    }

    function getFeatureVector(geometryArray, exception, avoidFavorID, groupID, set) {
      //  convert the geometry array into ALKMaps.Geometry.Point objects

      var lineStrings = _.map(geometryArray, (arr) => {
        var pts = _.map(arr, (geo) => {
          var pt = mapUtilities.LLtoMerc(geo[0], geo[1]);
          return new ALKMaps.Geometry.Point(pt.lon, pt.lat);
        });
        return new ALKMaps.Geometry.LineString(pts);
      });

      var feature = new ALKMaps.Feature.Vector(
        new ALKMaps.Geometry.MultiLineString(lineStrings), {
          exception: exception,
          enabled: true,
          avoidFavorID: avoidFavorID,
          groupID: groupID,
          isAvoidFavorFeature: true,
          set: set
        });
      feature.attributes.trueStyle = feature.style;
      return feature;
    }

    function frameBounds(bounds) {
      if (!bounds)
        return;
      mapService.getMap().zoomToExtent(bounds);
    }

    function frameAllExceptions(routeExceptionsLayer) {
      var bounds = routeExceptionsLayer.getDataExtent();
      frameBounds(bounds);
    }

    function frameRouteException(featuresByAvoidFavorID, featuresByGroupID, exception) {
      var bounds = _getBoundsForException(featuresByAvoidFavorID, featuresByGroupID, exception);
      frameBounds(bounds);
    }

    function frameRouteExceptions(featuresByAvoidFavorID, featuresByGroupID, exceptions) {
      var bounds = new ALKMaps.Bounds();
      _.forEach(exceptions, (exception) => {
        bounds.extend(_getBoundsForException(featuresByAvoidFavorID, featuresByGroupID, exception));
      });

      frameBounds(bounds);
    }

    function _getBoundsForException(featuresByAvoidFavorID, featuresByGroupID, exception) {
      var features = [];
      if (exception.GroupID === null) {
        features = featuresByAvoidFavorID[exception.GroupLinks[0].AvoidFavorID];
      } else {
        features = featuresByGroupID[exception.GroupID];
      }
      return _getFeatureBounds(features);
    }

    function _getFeatureBounds(features) {
      var bounds = new ALKMaps.Bounds();
      _.forEach(features, (feature) => {
        bounds.extend(feature.geometry.bounds);
      });

      return bounds;
    }
  }
})();



// WEBPACK FOOTER //
// ./Common Javascript Files/AngularJS/alk-route-exceptions/alkRouteException.layer.factory.js